import { Space, Typography } from 'antd';
import styled from 'styled-components';
import { WarningCircleSolid } from '../../icons';
import '../../error.css';
import { Content } from 'antd/lib/layout/layout';

const ErrorImg = styled(props => <WarningCircleSolid {...props} />)`
  height: 24px;
  width: 24px;
`

type ApiError = {
  status?: string;
  timestamp?: string;
  message: string;
  requestId?: string;
  errors?: Array<{
    code: string;
    message: string;
    reason?: string;
  }>;
};

function isObjectWithData(error: unknown): error is { data?: ApiError } {
  return !!error && typeof error === 'object' && 'data' in error;
}

function isValidApiError(data: ApiError | undefined): data is ApiError {
  return !!data && typeof data.status === 'string' && typeof data.message === 'string';
}

function parseStringError(error: string): { returnValue?: ApiError; data?: ApiError } {
  if (!error) return { returnValue: { message: '' } };
  try {
    const parsed = JSON.parse(error);
    const isPlainObject = parsed && typeof parsed === 'object' && !Array.isArray(parsed);
    if (isPlainObject) return { data: parsed as ApiError };
    return { returnValue: { message: error } };
  } catch {
    return { returnValue: { message: error } };
  }
}

function getFallbackMessage(error: any, data: ApiError | undefined): string {
  if (typeof error?.data === 'string') return error.data;
  if (typeof data?.message === 'string') return data.message;
  if (typeof error === 'string') return error;
  return 'An error occurred.';
}

function buildFallbackError(error: any, data: ApiError | undefined): ApiError {
  const status = typeof data?.status === 'string' ? data.status : String(error?.status ?? '');
  const message = getFallbackMessage(error, data);
  const errors = data && Array.isArray(data.errors) ? data.errors : [];
  return {
    status,
    timestamp: new Date().toISOString(),
    message,
    requestId: '',
    errors
  };
}

function getApiError(error: any): ApiError {
  let data: ApiError | undefined;

  if (typeof error === 'string') {
    const result = parseStringError(error);
    if (result.returnValue) return result.returnValue;
    data = result.data;
  } else if (isObjectWithData(error)) {
    data = error.data as ApiError | undefined;
  }

  if (isValidApiError(data)) return data;
  return buildFallbackError(error, data);
}

export interface ErrorContentProps {
  message?: string | Record<string, unknown>;
}

export const ErrorContent = ({ message = '' }: ErrorContentProps) => {
  const apiError: ApiError = getApiError(message);
  const displayText =
    apiError?.errors?.[0]?.message ??
    apiError?.message ??
    (typeof message === 'string' ? message : 'An error occurred.');

  return (
    <Space direction="vertical" className="error-page-content">
        <div className="error-page-content-body">
        <div className="error-icon-title-container">
          <ErrorImg />
          <Typography.Title level={3} className="error-page-title">Network Access Unavailable</Typography.Title>
        </div>
         <div>
          <Typography.Text className="error-page-body">{displayText}<br/>For assistance, please contact your network administrator.</Typography.Text>
          </div>
          </div>
      </Space>
  );
};
